import { Dispatch } from '@reduxjs/toolkit';
import { saveAs } from 'file-saver';
import { keyBy, uniqBy } from 'lodash/fp';

import { FlagIssuesFormData } from 'dashboard/forms/FormFlagIssues';
import { ResumeInquiryFormData } from 'dashboard/forms/FormResumeInquiry';
import { SendInquiryEmailFormData } from 'dashboard/forms/FormSendInquiryEmail';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { ReportIssueType } from 'dashboard/lib/constants/reportIssue';
import { Case } from 'dashboard/models/case';
import { Device } from 'dashboard/models/device';
import { Document } from 'dashboard/models/document';
import { Environment } from 'dashboard/models/environment';
import { Inquiry } from 'dashboard/models/inquiry';
import { InquirySession } from 'dashboard/models/inquiry-session';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { InquiryShowViewInquiryTemplateVersion } from 'dashboard/models/inquiry-template-version';
import { List } from 'dashboard/models/list';
import { ListItem } from 'dashboard/models/list-item';
import { ListMatch } from 'dashboard/models/list-match';
import { Report } from 'dashboard/models/report';
import { ReportTemplate } from 'dashboard/models/report-template';
import { Selfie } from 'dashboard/models/selfie';
import { Tag } from 'dashboard/models/tag';
import { Template } from 'dashboard/models/template';
import { User } from 'dashboard/models/user';
import {
  Verification,
  VERIFICATION_API_MODEL_TYPES_WITH_QA_VERIFICATION,
} from 'dashboard/models/verification';
import { Workflow } from 'dashboard/models/workflow';
import { WorkflowRun } from 'dashboard/models/workflow-run';
import { WorkflowRunStepAction } from 'dashboard/models/workflow-step';
import { WorkflowVersion } from 'dashboard/models/workflow-version';
import { setInquiry, setInquiryDetails } from 'dashboard/redux/actions/inquiry';
import { ApiEndpoint, ApiModel } from 'lib/interfaces/api';

export const loadInquiry = (inquiryId: string, environmentId: string) => {
  return async (dispatch: Dispatch) => {
    const inquiryResponse = await SessionClientDashboard.getResource<Inquiry>(
      ApiEndpoint.Inquiry,
      inquiryId,
      {
        headers: {
          'Persona-Environment-ID': environmentId,
        },
      },
    );

    const relationshipMap = keyBy('id', inquiryResponse.included);
    const inquiry = inquiryResponse.data;

    const devices = inquiry.relationships.devices.data.map(
      ({ id }) => relationshipMap[id],
    ) as Device[];
    const sessions = inquiry.relationships.sessions.data.map(
      ({ id }) => relationshipMap[id],
    ) as InquirySession[];

    const cases = inquiry.relationships.cases.data.map(({ id }) => relationshipMap[id]) as Case[];

    const documents =
      (inquiry.relationships.documents?.data.map(({ id }) => relationshipMap[id]) as Document[]) ||
      [];

    const selfies =
      (inquiry.relationships.selfies?.data.map(({ id }) => relationshipMap[id]) as Selfie[]) || [];

    const verifications = inquiry.relationships.verifications.data.map(
      ({ id }) => relationshipMap[id],
    ) as Verification[];

    const reports = inquiry.relationships.reports.data.map(
      ({ id }) => relationshipMap[id],
    ) as Report[];
    const reportTemplates = uniqBy(
      'id',
      reports.map(
        // TODO: Remove this once reports has been fully migrated to report-templates
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        (report) => relationshipMap[report.relationships?.['report-template']?.data?.id],
      ),
    ) as ReportTemplate[];

    const tags = inquiry.relationships.tags?.data.map(({ id }) => relationshipMap[id]) as Tag[];

    const listMatches = inquiry.relationships['list-matches'].data.map(
      ({ id }) => relationshipMap[id],
    ) as ListMatch[];

    const lists = listMatches.map((lm) => relationshipMap[lm.relationships.list.data.id]) as List[];
    const listItems = listMatches.map(
      (lm) => relationshipMap[lm.relationships['list-item'].data.id],
    ) as ListItem[];

    const creator =
      inquiry.relationships.creator.data != null
        ? (relationshipMap[inquiry.relationships.creator.data.id] as User)
        : undefined;

    const redactor =
      inquiry.relationships.redactor.data != null
        ? (relationshipMap[inquiry.relationships.redactor.data.id] as User)
        : undefined;

    let reviewer;
    switch (inquiry.relationships.reviewer.data?.type) {
      case ApiModel.WorkflowRun:
        reviewer = relationshipMap[inquiry.relationships.reviewer.data.id] as WorkflowRun;
        break;

      case ApiModel.User:
        reviewer = relationshipMap[inquiry.relationships.reviewer.data.id] as User;
        break;

      case ApiModel.WorkflowRunStepAction: {
        reviewer = relationshipMap[inquiry.relationships.reviewer.data.id] as WorkflowRunStepAction;
        break;
      }
      default:
        reviewer = undefined;
    }

    let reviewerWorkflow, reviewerWorkflowRun, reviewerWorkflowVersion;

    if (reviewer?.type === ApiModel.WorkflowRun) {
      // Workflows 1.0
      reviewerWorkflow = relationshipMap[reviewer.relationships.workflow.data.id] as Workflow;
      reviewerWorkflowVersion = relationshipMap[
        reviewer.relationships['workflow-version'].data.id
      ] as WorkflowVersion;
    } else if (reviewer?.type === ApiModel.WorkflowRunStepAction) {
      // Workflows 2.0 or Case Action
      reviewerWorkflowRun = relationshipMap[
        reviewer.relationships['workflow-run'].data.id
      ] as WorkflowRun;
      reviewerWorkflow = relationshipMap[
        reviewerWorkflowRun.relationships.workflow.data.id
      ] as Workflow;
      reviewerWorkflowVersion = relationshipMap[
        reviewerWorkflowRun.relationships['workflow-version'].data.id
      ] as WorkflowVersion;
    }

    const environment = relationshipMap[inquiry.relationships.environment.data.id] as Environment;
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const template = relationshipMap[inquiry.relationships.template.data?.id] as Template;
    const inquiryTemplate = relationshipMap[
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      inquiry.relationships['inquiry-template'].data?.id
    ] as InquiryTemplate;
    const inquiryTemplateVersion = relationshipMap[
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      inquiry.relationships['inquiry-template-version'].data?.id
    ] as InquiryShowViewInquiryTemplateVersion;

    // Dispatch details before inquiry so we can safely use presence of inquiry
    // to indicate that all data are fetched
    dispatch(
      setInquiryDetails({
        cases,
        creator,
        devices,
        documents,
        environment,
        listItems,
        listMatches,
        lists,
        redactor,
        reports,
        reportTemplates,
        reviewer,
        reviewerWorkflow,
        reviewerWorkflowRun,
        reviewerWorkflowVersion,
        selfies,
        sessions,
        tags,
        template: inquiryTemplate ?? template,
        inquiryTemplateVersion,
        verifications,
      }),
    );
    dispatch(setInquiry(inquiry));
  };
};

export const printInquiry = (id: string) => {
  return async () => {
    const blob = await SessionClientDashboard.getBlob(ApiEndpoint.Inquiry, id, 'print');
    saveAs(blob, `${id}.pdf`);
  };
};

export const redactInquiry = (id: string) => {
  return async () => {
    return await SessionClientDashboard.deleteResource<Inquiry>(ApiEndpoint.Inquiry, id);
  };
};

export const approveInquiry = (id: string, comment: string) => {
  return async () => {
    const payload = {
      type: ApiModel.Inquiry as const,
      id: id,
    };
    const meta = {
      comment,
    };
    const approveInquiryResponse = await SessionClientDashboard.postResourceAction<Inquiry>(
      payload,
      'approve',
      {
        meta,
      },
    );

    return approveInquiryResponse;
  };
};

export const declineInquiry = (id: string, comment: string) => {
  return async () => {
    const payload = {
      type: ApiModel.Inquiry as const,
      id: id,
    };
    const meta = {
      comment,
    };
    const declineInquiryResponse = await SessionClientDashboard.postResourceAction<Inquiry>(
      payload,
      'decline',
      {
        meta,
      },
    );

    return declineInquiryResponse;
  };
};

export const flagInquiry = (
  inquiry: Inquiry,
  verifications: Verification[],
  { issueVerifications, issueType, content, responseType, addAsFraudulent }: FlagIssuesFormData,
) => {
  return async () => {
    let addAsFraudulentText = 'none';

    let verificationGroundTruth;
    switch (issueType) {
      case ReportIssueType.Classification:
        verificationGroundTruth = issueVerifications.map((issue) => ({
          'verification-id': issue.verificationId,
          'ground-truth': issue.correctValue,
        }));
        break;
      case ReportIssueType.Extraction:
        verificationGroundTruth = issueVerifications.map((issue) => ({
          'verification-id': issue.verificationId,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'ground-truth': { [issue.attributeName]: issue.correctValue },
        }));
        break;
      case ReportIssueType.Fraud:
        addAsFraudulentText = addAsFraudulent ? 'fraud' : 'none';
        // Flag all verifications that have QA support for inquiry as fraud
        verificationGroundTruth = verifications
          .filter((ver) => VERIFICATION_API_MODEL_TYPES_WITH_QA_VERIFICATION.includes(ver.type))
          .map((ver) => ({
            'verification-id': ver.id,
            'ground-truth': { status: 'failed' },
          }));
        break;
    }

    await SessionClientDashboard.postResourceAction<Inquiry>(
      { id: inquiry.id, type: inquiry.type },
      'flag-issues',
      {
        meta: {
          'issue-type': issueType,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'issue-verifications': verificationGroundTruth,
          content: content,
          'response-type': responseType,
          'add-to-global-registry': addAsFraudulentText,
        },
      },
    );
  };
};

export const resumeInquiry = (
  id: string,
  { sendEmailAddress, emailAddress }: ResumeInquiryFormData,
) => {
  return async () => {
    const inquiryResponse = await SessionClientDashboard.postResourceAction<Inquiry>(
      {
        type: ApiModel.Inquiry,
        id: id,
      },
      'generate-one-time-link',
      {
        meta: {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          email_address: sendEmailAddress ? emailAddress : null,
        },
      },
    );
    return inquiryResponse;
  };
};

export const sendInquiryEmail = (id: string, data: SendInquiryEmailFormData) => {
  return async () => {
    const sendInquiryEmailResponse = await SessionClientDashboard.postResourceAction<Inquiry>(
      {
        type: ApiModel.Inquiry as const,
        id: id,
      },
      'email-inquiry-flow-link',
      {
        meta: {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'email-address': data['email-address'],
          'email-cc-addresses': data['email-cc-addresses'] || [],
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'email-subject': data['email-subject'],
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'email-greeting': data['email-greeting'],
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'email-body': data['email-body'],
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          'email-cta': data['email-cta'],
        },
      },
    );
    return sendInquiryEmailResponse;
  };
};
